import React, { Fragment, useEffect, useState } from 'react';
import BrandReviews from './brand-reviews';
import { apiService } from 'utils/api';
import { BrandType } from 'models/brand.model';
import BrandSelector from 'components/base/brand-selector';
import UnresolvedModal from 'components/cs/pending-tasks/review-modal/unresolved-modal';
import ResolvedModal from 'components/cs/pending-tasks/review-modal/resolved-modal';
import useDashboardContext from 'contexts/dashboard.context';
import {
  Dropdown,
  DropdownItem,
  DropdownList,
  ENUM_DATE_RANGE,
  getDateRangeSelector
} from '@cokitchen/cokitchen-components';
import { RatingsType } from 'models/review.model';
import BrandRating from 'components/cs/brand-rating';
import { normalizeRating } from 'utils/functions';
import { ReactComponent as FilterIcon } from 'assets/icons/sort.svg';

const baseUrl = process.env.REACT_APP_ORDER_STATS_API_KEY;

const Reviews = () => {
  const { activeCokitchen } = useDashboardContext();
  const [loading, setLoading] = useState(true);
  const [brands, setBrands] = useState<BrandType[]>([]);
  const [activeBrand, setActiveBrand] = useState({ name: 'All', id: 'ALL' } as BrandType);
  const [refreshKey, setRefreshKey] = useState(0);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showResolvedModal, setShowResolvedModal] = useState(false);
  const [filters, setFilters] = useState(getDateRangeSelector(ENUM_DATE_RANGE.THIS_WEEK));
  const [ratingsLoading, setRatingsLoading] = useState(false);
  const [ratingsData, setRatingsData] = useState<RatingsType>();
  const [ratingFilter, setRatingFilter] = useState<null | number>(null);
  const [openRatingDropdown, setOpenRatingDropdown] = useState(false);

  const FILTER_VALUES = [1, 2, 3, 4, 5];

  const toggleResolved = () => {
    setShowResolvedModal(!showResolvedModal);
  };

  const toggleUnresolved = () => {
    setShowReviewModal(!showReviewModal);
  };

  const fetchBrands = async () => {
    try {
      setLoading(true);
      const res = await apiService.get(`marketing/brand/reviews/${activeCokitchen.id}`, {
        baseUrl
      });

      const brandReviews = res?.data?.data?.brand_reviews ?? [];

      setBrands(brandReviews);
    } finally {
      setLoading(false);
    }
  };

  const getCsatAndReviewCount = async () => {
    const { startDate: start_date, endDate: end_date } = filters;
    const params: any = {
      start_date,
      end_date,
      ...(activeBrand.id !== 'ALL' && { brand_id: activeBrand.id })
    };
    try {
      setRatingsLoading(true);
      const res = await apiService.get(`marketing/${activeCokitchen.id}/csat-scores`, {
        params,
        baseUrl: process.env.REACT_APP_ORDER_STATS_API_KEY
      });
      if (res.data === '') {
        setRatingsData(undefined);
      } else {
        setRatingsData(res.data);
      }
    } finally {
      setRatingsLoading(false);
    }
  };

  useEffect(() => {
    getCsatAndReviewCount();
  }, [activeBrand, filters]);

  useEffect(() => {
    fetchBrands();
    getCsatAndReviewCount();
  }, []);

  useEffect(() => {
    setRefreshKey(refreshKey + 1);
  }, [activeBrand, brands]);
  const totalRatings = Number(
    ratingsData?.ratings?.reduce((acc, rating) => acc + parseInt(rating.total_review), 0)
  );

  return (
    <Fragment>
      <div className='w-full flex justify-between gap-4 flex-wrap'>
        <div className='w-[50%] flex justify-between flex-wrap gap-4'>
          <div className='mb-6'>
            <h1 className='font-semibold text-2xl mb-2'>Reviews</h1>
            <BrandSelector
              label='Select Brand'
              allBrands={brands}
              activeBrand={activeBrand}
              setActiveBrand={setActiveBrand}
            />
          </div>
          <div>
            {ratingsLoading ? (
              <div className='h-[10vh] flex justify-center items-center'>
                <p>Loading rating data</p>
              </div>
            ) : ratingsData === undefined ? (
              <div className='h-[10vh] flex justify-center items-center'>
                <p>No rating data available</p>
              </div>
            ) : (
              normalizeRating(ratingsData?.ratings ?? []).map((rating, index) => (
                <BrandRating key={index} rating={rating} totalRatings={totalRatings} />
              ))
            )}
          </div>
        </div>
        <div className='flex items-center justify-between gap-8'>
          {ratingsLoading ? (
            <p>Loading...</p>
          ) : (
            <div className='flex justify-end gap-6'>
              {Number(ratingsData?.ratings?.length) < 1 ? (
                <div className='h-[10vh] flex justify-center items-center'>
                  <p>No CSAT data available</p>
                </div>
              ) : (
                <>
                  <div>
                    <p className='text-[#1A1A1A] text-xl'>Reviews: {ratingsData?.total_reviews}</p>
                    {ratingsData?.review_percentage_change &&
                    ratingsData?.review_percentage_change !== 0 ? (
                      <p className='text-gray-8 text-[11px]'>
                        <span> {ratingsData?.review_percentage_change} % </span>
                        {Number(ratingsData?.review_percentage_change) >= 0
                          ? 'increase'
                          : 'decrease'}
                      </p>
                    ) : null}
                  </div>
                  <div>
                    <p className='text-[#1A1A1A] text-xl'>
                      CSAT: {ratingsData?.total_csat === 'NaN' ? '0' : ratingsData?.total_csat}%
                    </p>
                    {ratingsData?.csat_percentage_change &&
                    ratingsData?.csat_percentage_change !== 0 ? (
                      <p className='text-gray-8 text-[11px]'>
                        <span> {ratingsData?.csat_percentage_change} % </span>
                        {Number(ratingsData?.csat_percentage_change) >= 0 ? 'increase' : 'decrease'}
                      </p>
                    ) : null}
                  </div>
                </>
              )}
            </div>
          )}
          <Dropdown
            visible={openRatingDropdown}
            onClickOutside={() => setOpenRatingDropdown(false)}
            placement='bottom-start'
            content={
              <div
                onClick={() => setOpenRatingDropdown(!openRatingDropdown)}
                className='border border-[#E5E6E6] rounded-[6px] text-sm p-3 flex gap-3 items-center justify-between min-w-[114px] cursor-pointer'>
                <span className='text-[#5E6164] whitespace-nowrap'>
                  {ratingFilter ? `${ratingFilter} star ratings` : 'Sort by'}
                </span>
                <FilterIcon className='text-[#5E6164]' />
              </div>
            }>
            <DropdownList>
              {ratingFilter && (
                <DropdownItem
                  onClick={() => {
                    setRatingFilter(null);
                    setOpenRatingDropdown(false);
                  }}>
                  All
                </DropdownItem>
              )}
              {FILTER_VALUES.map((rating: number, index: number) => (
                <DropdownItem
                  key={index}
                  onClick={() => {
                    setRatingFilter(rating);
                    setOpenRatingDropdown(false);
                  }}>
                  {rating} star ratings
                </DropdownItem>
              ))}
            </DropdownList>
          </Dropdown>
        </div>
      </div>
      {loading ? (
        <div className='text-2xl leading-[40vh] text-center'>Loading....</div>
      ) : (
        <BrandReviews
          key={refreshKey}
          activeBrand={activeBrand}
          setActiveBrand={setActiveBrand}
          allBrands={brands}
          filters={filters}
          setFilters={setFilters}
          rating={ratingFilter}
        />
      )}

      <UnresolvedModal show={showReviewModal} onClose={toggleUnresolved} />

      <ResolvedModal show={showResolvedModal} onClose={toggleResolved} />
    </Fragment>
  );
};

export default Reviews;
